/**
 * Timeout wrapper for API services
 * Provides consistent timeout handling across all service files
 */

import axios from "axios";
import { withTimeout, getTimeoutForOperation, TIMEOUT_CONFIG } from "./api";

/**
 * Create a timeout-aware axios instance
 * @param {Object} defaultConfig - Default configuration for the instance
 * @returns {Object} - Axios instance with timeout handling
 */
export const createTimeoutAxios = (defaultConfig = {}) => {
  const instance = axios.create(defaultConfig);

  // Add request interceptor to handle timeouts
  instance.interceptors.request.use(
    (config) => {
      // If timeout is not specified, determine based on operation
      if (!config.timeout) {
        const operationType = determineOperationType(config);
        config.timeout = getTimeoutForOperation(operationType);
      }

      // Add AbortController if not present
      if (!config.signal) {
        const controller = new AbortController();
        config.signal = controller.signal;
        config._controller = controller;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Add response interceptor to clean up
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Enhanced error for timeouts
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        const enhancedError = new Error(
          `Request timeout: ${error.config?.url || "Unknown URL"}`,
        );
        enhancedError.code = "TIMEOUT";
        enhancedError.timeout = error.config?.timeout;
        enhancedError.url = error.config?.url;
        enhancedError.method = error.config?.method;
        throw enhancedError;
      }
      throw error;
    },
  );

  return instance;
};

/**
 * Determine operation type based on request config
 * @param {Object} config - Axios request config
 * @returns {string} - Operation type
 */
const determineOperationType = (config) => {
  const { method, url, data, headers } = config;

  // File upload detection
  if (headers?.["Content-Type"]?.includes("multipart/form-data")) {
    return "upload";
  }

  // Download detection
  if (config.responseType === "blob" || config.responseType === "stream") {
    return "download";
  }

  // Batch operation detection
  if (url?.includes("/batch") || data?.ids?.length > 10) {
    return "batch";
  }

  // Quick operations for GET by ID or DELETE
  if ((method === "GET" && url?.match(/\/\d+$/)) || method === "DELETE") {
    return "quick";
  }

  // Default to normal
  return "normal";
};

/**
 * Wrap an API method with timeout handling
 * @param {Function} apiMethod - The API method to wrap
 * @param {Object} options - Timeout options
 * @returns {Function} - Wrapped API method
 */
export const withTimeoutHandler = (apiMethod, options = {}) => {
  return async (...args) => {
    const { timeout, operationType } = options;
    const effectiveTimeout =
      timeout || getTimeoutForOperation(operationType || "normal");

    // Create abort controller
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, effectiveTimeout);

    try {
      // Call the original method with controller
      const result = await apiMethod(...args, { signal: controller.signal });
      clearTimeout(timeoutId);
      return result;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort errors
      if (error.name === "AbortError") {
        const timeoutError = new Error(
          `Operation timed out after ${effectiveTimeout}ms`,
        );
        timeoutError.code = "TIMEOUT";
        timeoutError.timeout = effectiveTimeout;
        throw timeoutError;
      }

      throw error;
    }
  };
};

/**
 * Create a service wrapper with consistent timeout handling
 * @param {Object} serviceMethods - Object containing service methods
 * @param {Object} serviceConfig - Service configuration
 * @returns {Object} - Wrapped service methods
 */
export const createTimeoutService = (serviceMethods, serviceConfig = {}) => {
  const wrappedService = {};

  Object.keys(serviceMethods).forEach((methodName) => {
    const method = serviceMethods[methodName];

    if (typeof method === "function") {
      // Determine timeout based on method name
      let operationType = "normal";

      if (methodName.includes("import") || methodName.includes("upload")) {
        operationType = "upload";
      } else if (
        methodName.includes("export") ||
        methodName.includes("download")
      ) {
        operationType = "download";
      } else if (methodName.includes("batch")) {
        operationType = "batch";
      } else if (methodName === "getById" || methodName === "delete") {
        operationType = "quick";
      }

      // Apply wrapper
      wrappedService[methodName] = withTimeoutHandler(method, {
        operationType,
        ...serviceConfig[methodName],
      });
    } else {
      // Copy non-function properties as-is
      wrappedService[methodName] = method;
    }
  });

  return wrappedService;
};

/**
 * Cancel all pending requests for a service
 * @param {Object} requests - Map of request controllers
 */
export const cancelAllRequests = (requests) => {
  Object.values(requests).forEach((controller) => {
    if (controller && typeof controller.abort === "function") {
      controller.abort();
    }
  });
};

/**
 * Create a request manager for tracking and cancelling requests
 * @returns {Object} - Request manager
 */
export const createRequestManager = () => {
  const requests = new Map();

  return {
    add: (key, controller) => {
      // Cancel existing request with same key
      if (requests.has(key)) {
        requests.get(key).abort();
      }
      requests.set(key, controller);
    },

    remove: (key) => {
      requests.delete(key);
    },

    cancel: (key) => {
      const controller = requests.get(key);
      if (controller) {
        controller.abort();
        requests.delete(key);
      }
    },

    cancelAll: () => {
      requests.forEach((controller) => controller.abort());
      requests.clear();
    },

    size: () => requests.size,
  };
};

export default {
  createTimeoutAxios,
  withTimeoutHandler,
  createTimeoutService,
  cancelAllRequests,
  createRequestManager,
  TIMEOUT_CONFIG,
};
